import React, { ReactNode, useEffect, createContext, useContext, useState, useRef } from "react"
import ReactDOM from "react-dom"
import { useBlocker, useLocation, useNavigate } from "react-router-dom"

import "./Modal.module.css"

const lockScrollClass = 'lock-scroll'

interface Modal {
    id: string
    content: ReactNode
}

interface ModalContextType {
    modals: Modal[]
    addModal: (modal: Modal) => void
    removeModal: (id: string) => void
    removeTopModal: () => void
    removeAllModals: () => void
    openModal: (Component: React.ComponentType<any>) => void
    refreshModals: () => void
}

// Create context with default value
const ModalContext = createContext<ModalContextType | undefined>(undefined)

// Modal Provider Component
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [modals, setModals] = useState<Modal[]>([])

    const addModal = (modal: Modal) => {
        setModals((prev) => [...prev, modal])
    }

    const removeModal = (id: string) => {
        setModals((prev) => prev.filter((modal) => modal.id !== id))
    }

    const removeTopModal = () => {
        setModals((prev) => {
            if (prev.length === 0) return prev
            return prev.slice(0, prev.length - 1)
        })
    }

    const removeAllModals = () => {
        setModals([])
    }

    const openModal = (Component: React.ComponentType<any>) => {
        const modalId = Math.random().toString(36).substring(7)
        addModal({
            id: modalId,
            content: <Component onClose={() => removeModal(modalId)} />,
        })
    }

    const refreshModals = () => {
        setModals((prev) => [...prev])
    }

    return (
        <ModalContext.Provider value={{ modals, addModal, removeModal, removeTopModal, removeAllModals, openModal, refreshModals }}>
            {children}
        </ModalContext.Provider>
    )
}

// Hook for consuming the ModalContext
export const useModal = (): ModalContextType => {
    const context = useContext(ModalContext)
    if (!context) {
        throw new Error("useModal must be used within a ModalProvider")
    }
    return context
}

type ModalWrapperProps = {
    isOpen: boolean
    onClose: () => void
    children: ReactNode
}

export const ModalWrapper: React.FC<ModalWrapperProps> = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null

    return <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={onClose}>
                &times;
            </button>
            {children}
        </div>
    </div>

    // return ReactDOM.createPortal(
    //     <div className="modal-overlay" onClick={onClose}>
    //         <div className="modal-content" onClick={(e) => e.stopPropagation()}>
    //             <button className="modal-close" onClick={onClose}>
    //                 &times;
    //             </button>
    //             {children}
    //         </div>
    //     </div>,
    //     document.body
    // )
}

export const GlobalModalContainer: React.FC = () => {
    const { modals, removeModal, removeAllModals } = useModal()
    const location = useLocation()
    const previousLocationRef = useRef(location)

    useEffect(() => {
        const previousLocation = previousLocationRef.current
        if (previousLocation.pathname !== location.pathname) {
            previousLocationRef.current = location
            removeAllModals()
        }
    }, [location])

    useEffect(() => {
        if (modals.length > 0) {
            document.body.classList.add(lockScrollClass)
        } else {
            document.body.classList.remove(lockScrollClass)
        }
        return () => {
            document.body.classList.remove(lockScrollClass)
        }
    }, [modals])

    return (
        <div>
            {modals.map(({ id, content }) => (
                <ModalWrapper key={id} isOpen={true} onClose={() => removeModal(id)}>
                    {content}
                </ModalWrapper>
            ))}
        </div>
    )
}